<template>
	<v-container class="pt-0">
		<v-card elevation="0" class="ant-card px-4 pb-2">
			<v-card-title class="pa-0">
				<v-col class="d-flex align-center" cols="auto">
					<div class="v-typography--title1">
						{{ $t("stock_images") }}
					</div>
				</v-col>
				<v-spacer></v-spacer>
				<v-col class="d-flex justify-end px-0" cols="auto">
					<v-btn v-if="!deleteMode" text outlined @click="enableDelete" class="mx-1">
						<v-icon size="17" color="red">icon-delete</v-icon>
						<span class="ml-1 v-typography--body-sm">{{ $t("delete") }}</span>
					</v-btn>
					<v-btn v-if="deleteMode" text outlined @click="cancelDelete" class="mx-1">
						<v-icon size="15">icon-cross</v-icon>
						<span class="ml-1 v-typography--body-sm">{{ $t("cancel") }}</span>
					</v-btn>
					<v-btn v-if="deleteMode" text outlined @click="saveDelete" class="mx-1">
						<v-icon size="15">icon-check</v-icon>
						<span class="ml-1 v-typography--body-sm">{{ $t("save") }}</span>
					</v-btn>
					<v-btn text outlined @click="openFileDialog" class="mx-1">
						<v-icon size="15">icon-plus</v-icon>
						<span class="ml-1 v-typography--body-sm">{{ $t("add_images") }}</span>
					</v-btn>
					<input
						ref="fileInput"
						type="file"
						multiple
						style="display: none"
						accept="image/*"
						@change="handleFileSelected"
					/>
				</v-col>
			</v-card-title>
			<v-divider class="mb-4" />
			<v-form v-model="valid" ref="form" class="py-0">
				<v-row v-if="imagesLoaded.length === 0">
					<v-col class="text-center mt-4">
						<v-card class="no-data-message text-center ant-card py-3" elevation="0">
							<v-icon class="icon-empty-state" size="28" color="black"></v-icon>
							<v-col>
								<p class="font-weight-bold mb-1 black--text v-typography--body-sm">
									{{ $t("no_entity", { entityName: $t("images") }) }}
								</p>
								<span class="v-typography--body-sm no-data-message">
									{{ $t("no_entity_message", { entityName: $t("images") }) }}
								</span>
							</v-col>
						</v-card>
					</v-col>
				</v-row>
				<v-row v-else>
					<v-col
						v-for="(imgUrl, idx) in imagesLoaded"
						:key="idx"
						cols="auto"
						class="pa-4 pt-0 d-flex flex-column align-center"
					>
						<v-img :src="imgUrl" width="140" height="140" class="pointer" @click="openGallery(idx)" />
						<v-checkbox
							v-if="deleteMode"
							v-model="selectedForDelete"
							:value="idx"
							class="mt-2"
							dense
							hide-details
							on-icon="icon-checkbox-checked"
							off-icon="icon-checkbox-unchecked"
						/>
					</v-col>
				</v-row>
			</v-form>
		</v-card>
		<div
			v-if="showGallery"
			class="fullscreen-overlay d-flex flex-column justify-center align-center"
			@click="closeOnBackground"
		>
			<v-btn icon @click.stop="closeGallery" class="gallery-close-btn">
				<v-icon color="white">icon-cross</v-icon>
			</v-btn>
			<v-btn icon @click.stop="prevImage" class="gallery-arrow-left">
				<v-icon color="white">icon-angle-left</v-icon>
			</v-btn>
			<v-btn icon @click.stop="nextImage" class="gallery-arrow-right">
				<v-icon color="white">icon-angle-right</v-icon>
			</v-btn>
			<v-img :src="imagesLoaded[currentImageIndex]" max-width="90%" max-height="90%" contain @click.stop />
		</div>
	</v-container>
</template>

<script>
import remote from "../../../data/remote";

export default {
	name: "StockImages",
	props: {
		stockId: { type: String, required: true },
		images: { type: Array, default: () => [] },
	},
	data() {
		return {
			valid: true,
			deleteMode: false,
			localKeys: [],
			imagesLoaded: [],
			selectedForDelete: [],
			showGallery: false,
			currentImageIndex: 0,
		};
	},
	created() {
		this.localKeys = [...this.images];
		this.fetchAllKeys();
	},
	methods: {
		async fetchAllKeys() {
			this.imagesLoaded = [];
			for (const key of this.localKeys) {
				try {
					const blob = await this.loadStockImageBlob(key);
					const url = window.URL.createObjectURL(blob);
					this.imagesLoaded.push(url);
				} catch (err) {
					console.error("Failed to load stock image:", err);
				}
			}
		},
		loadStockImageBlob(imgKey) {
			return new Promise((resolve, reject) => {
				remote.files.get(imgKey, {
					onSuccess: (result) => {
						const blob = new Blob([result]);
						resolve(blob);
					},
					onFail: (error) => reject(error),
				});
			});
		},
		openFileDialog() {
			this.$refs.fileInput.click();
		},
		handleFileSelected(event) {
			const files = [...event.target.files];
			if (!files.length) return;
			this.uploadFiles(files);
			event.target.value = null;
		},
		async uploadFiles(files) {
			try {
				const newKeys = [];
				for (const file of files) {
					const key = await this.uploadSingleFile(file);
					newKeys.push(key);
				}
				const updatedKeys = [...this.localKeys, ...newKeys];
				await this.updateStockImagesOnServer(updatedKeys);
				this.localKeys = updatedKeys;
				this.fetchAllKeys();
			} catch (error) {
				console.error("Error uploading images:", error);
			}
		},
		uploadSingleFile(file) {
			return new Promise((resolve, reject) => {
				remote.files.send(file, "stockImages", {
					onSuccess: (res) => resolve(res.key),
					onFail: (err) => reject(err),
				});
			});
		},
		enableDelete() {
			this.deleteMode = true;
			this.selectedForDelete = [];
		},
		cancelDelete() {
			this.deleteMode = false;
			this.selectedForDelete = [];
		},
		async saveDelete() {
			try {
				const updated = this.localKeys.filter((_, i) => !this.selectedForDelete.includes(i));
				await this.updateStockImagesOnServer(updated);
				this.localKeys = updated;
				this.fetchAllKeys();
				this.deleteMode = false;
				this.selectedForDelete = [];
			} catch (err) {
				console.error("Failed to delete images:", err);
			}
		},
		updateStockImagesOnServer(imagesArr) {
			return new Promise((resolve, reject) => {
				remote.stocks.update(
					{
						id: this.stockId,
						images: imagesArr,
						partialUpdate: true,
					},
					{
						onSuccess: (updatedStock) => resolve(updatedStock),
						onFail: (error) => {
							console.error("Failed to update stock images:", error);
							reject(error);
						},
					}
				);
			});
		},
		openGallery(idx) {
			this.currentImageIndex = idx;
			this.showGallery = true;
			window.addEventListener("keydown", this.handleKeydown);
		},
		closeGallery() {
			this.showGallery = false;
			window.removeEventListener("keydown", this.handleKeydown);
		},
		closeOnBackground() {
			this.closeGallery();
		},
		prevImage() {
			this.currentImageIndex = (this.currentImageIndex - 1 + this.imagesLoaded.length) % this.imagesLoaded.length;
		},
		nextImage() {
			this.currentImageIndex = (this.currentImageIndex + 1) % this.imagesLoaded.length;
		},
		handleKeydown(e) {
			if (e.key === "ArrowLeft") {
				this.prevImage();
			} else if (e.key === "ArrowRight") {
				this.nextImage();
			} else if (e.key === "Escape") {
				this.closeGallery();
			}
		},
	},
	watch: {
		images(newVal) {
			this.localKeys = [...newVal];
			this.fetchAllKeys();
		},
	},
};
</script>

<style scoped>
.fullscreen-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.8);
	z-index: 9999;
	cursor: pointer;
}
.gallery-close-btn {
	position: absolute;
	top: 50px;
	right: 100px;
}
.gallery-arrow-left {
	position: absolute;
	top: 50%;
	left: 100px;
	transform: translateY(-50%);
}
.gallery-arrow-right {
	position: absolute;
	top: 50%;
	right: 100px;
	transform: translateY(-50%);
}
.pointer {
	cursor: pointer;
}
@media (max-width: 600px) {
	.gallery-close-btn {
		top: 2%;
		right: 2%;
	}
	.gallery-arrow-left {
		left: 2%;
	}
	.gallery-arrow-right {
		right: 2%;
	}
}
</style>
